import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import BackIcon from '../../assets/icons/BackIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import { useAppearance } from '../../hooks/useAppearance';
import { useModalError } from '../../hooks/useModalError';
import { useRouteState } from '../../hooks/useRouteState';
import { ThemeContainer } from '../../styles';
import { isMobile, roundToEven } from '../../utils';
import FocusTrap from '../focusTrap';
import Footer from '../footer';
import LockBody from '../lockBody';
import Portal from '../portal';
import {
  BackButton,
  BoxContainer,
  CloseButton,
  ControllerContainer,
  ErrorContent,
  ErrorMessage,
  ModalContainer,
  ModalHeading,
  ModalOverlay,
  ModalRoot,
  PageContainer,
  PageContents,
} from './styles';

const RO = typeof window !== 'undefined' && window.ResizeObserver ? window.ResizeObserver : ResizeObserverPolyfill;

type ModalProps = {
  children: ReactNode;
  open?: boolean;
  title?: string | FC;
  inlineSelectorId?: string;
  onClose?: () => void;
  onBack?: () => void;
};

const Modal = (props: ModalProps) => {
  const { children, open, title: ModalTitle, onClose, onBack } = props;
  const route = useRouteState((state) => state.route);
  const { appearance } = useAppearance();
  const mobile = isMobile();
  const inlineSelectorId = appearance.inlineSelectorId;
  const { error } = useModalError();

  const [mounted, setMounted] = useState(false);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setRendered(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setRendered(false);
      const timer = setTimeout(() => setMounted(false), 160);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const ref = useRef<any>(null);
  const [dimensions, setDimensions] = useState<{
    width: string | undefined;
    height: string | undefined;
  }>({
    width: undefined,
    height: undefined,
  });

  const updateBounds = (node: any) => {
    const bounds = {
      width: node?.offsetWidth || undefined,
      height: node?.offsetHeight || undefined,
    };
    setDimensions({
      width: `${roundToEven(bounds?.width)}px`,
      height: `${roundToEven(bounds?.height)}px`,
    });
  };

  const contentRef = useCallback((node: any) => {
    if (!node) return;
    ref.current = node;
    updateBounds(node);
  }, []);

  const dimensionsCSS = useMemo(
    () =>
      ({
        '--height': dimensions.height,
        '--width': dimensions.width,
      }) as React.CSSProperties,
    [dimensions]
  );

  useEffect(() => {
    if (ref.current) updateBounds(ref.current);
  }, [route.path]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new RO((entries) => {
      for (const entry of entries) {
        if (entry.target === element) {
          updateBounds(ref.current);
        }
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, [ref.current]);

  useEffect(() => {
    if (!mounted) {
      setDimensions({
        width: undefined,
        height: undefined,
      });
      return;
    }

    if (appearance.isDismissable !== false) {
      const listener = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && onClose) onClose();
      };
      document.addEventListener('keydown', listener);
      return () => {
        document.removeEventListener('keydown', listener);
      };
    }
    return;
  }, [mounted, onClose, appearance.isDismissable]);

  const Content: ReactNode = (
    <ThemeContainer $useTheme={appearance?.theme} $useMode={appearance?.mode}>
      <ModalRoot
        style={{
          pointerEvents: rendered ? 'auto' : 'none',
          position: inlineSelectorId ? 'relative' : undefined,
        }}
      >
        {!inlineSelectorId && (
          <ModalOverlay $active={rendered} onClick={appearance.isDismissable !== false ? onClose : undefined} />
        )}
        <ModalContainer style={dimensionsCSS} initial={false} $positionInside={Boolean(inlineSelectorId)}>
          <BoxContainer
            className={`${rendered && 'active'}`}
            initial={false}
            $positionInside={Boolean(inlineSelectorId)}
          >
            <ControllerContainer>
              <AnimatePresence>
                {onBack && (
                  <BackButton
                    onClick={onBack}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: mobile ? 0 : 0.1,
                      delay: mobile ? 0.01 : 0,
                    }}
                  >
                    <BackIcon />
                  </BackButton>
                )}
              </AnimatePresence>

              {onClose && !inlineSelectorId && (
                <CloseButton onClick={onClose}>
                  <CloseIcon />
                </CloseButton>
              )}
            </ControllerContainer>

            {ModalTitle && <ModalHeading>{typeof ModalTitle === 'string' ? ModalTitle : <ModalTitle />}</ModalHeading>}

            <PageContainer>
              <PageContents ref={contentRef}>
                <ErrorMessage $display={Boolean(error)}>
                  <ErrorContent>
                    <ErrorIcon />
                    {error}
                  </ErrorContent>
                </ErrorMessage>

                {children}

                <Footer></Footer>
              </PageContents>
            </PageContainer>
          </BoxContainer>
        </ModalContainer>
      </ModalRoot>
    </ThemeContainer>
  );

  return (
    mounted && (
      <>
        {inlineSelectorId ? (
          <Portal selector={inlineSelectorId}>{Content}</Portal>
        ) : (
          <Portal>
            <FocusTrap>
              <LockBody>{Content}</LockBody>
            </FocusTrap>
          </Portal>
        )}
      </>
    )
  );
};

export default Modal;
